import * as RS from '@radix-ui/react-select'
import * as RT from '@radix-ui/react-tabs'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Check, ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/utils'

/* Radix rather than a native <select>.
 *
 * A native select cannot be styled inside the popup on any browser, so its
 * options always render in the OS theme -- white rows on a dark app. On a
 * screen full of dark surfaces that one white rectangle is the first thing the
 * eye finds, which is the opposite of what a workspace picker should do. */

export function Select({
  value, onValueChange, items, placeholder, className, size = 'md', ariaLabel, disabled,
}: {
  value: string
  onValueChange: (v: string) => void
  items: {
    value: string
    label: string
    hint?: string
    thumbnail?: ReactNode
    thumbnailPreview?: ReactNode
  }[]
  placeholder?: string
  className?: string
  size?: 'sm' | 'md'
  ariaLabel?: string
  disabled?: boolean
}) {
  const selected = items.find(item => item.value === value)

  return (
    <Tooltip.Provider delayDuration={250}>
	<RS.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <RS.Trigger
        aria-label={ariaLabel}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-[--radius-control]',
          'border border-line bg-surface-2 text-ink transition-colors',
          'hover:border-line-strong focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25',
          'data-[placeholder]:text-ink-faint disabled:opacity-50',
          size === 'sm' ? 'h-8 px-2.5 text-[13px]' : 'h-9.5 px-3 text-sm',
          className,
        )}
      >
        {selected ? (
          <SelectPreview preview={selected.thumbnailPreview}>
            <span className="flex min-w-0 items-center gap-2">
              {selected.thumbnail}
              <span className="truncate">{selected.label}</span>
            </span>
          </SelectPreview>
        ) : <RS.Value placeholder={placeholder} />}
        <RS.Icon><ChevronDown className="size-4 opacity-60" /></RS.Icon>
      </RS.Trigger>

      <RS.Portal>
        <RS.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'z-50 max-h-80 min-w-[var(--radix-select-trigger-width)] overflow-hidden',
            'rounded-[--radius-card] border border-line bg-surface-1 p-1',
            'shadow-[0_16px_48px_oklch(0_0_0/0.5)]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          <RS.Viewport>
            {items.map(it => (
              <Tooltip.Root key={it.value}>
                <Tooltip.Trigger asChild>
                  <RS.Item
                    value={it.value}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center gap-2 rounded-[--radius-control]',
                      'py-1.5 pl-8 pr-3 text-sm text-ink outline-none',
                      'data-[highlighted]:bg-surface-3 data-[state=checked]:text-brand',
                    )}
                  >
                    <span className="absolute left-2 top-2">
                      <RS.ItemIndicator><Check className="size-3.5" /></RS.ItemIndicator>
                    </span>
                    {it.thumbnail}
                    <span className="min-w-0 flex-1">
                      <RS.ItemText>{it.label}</RS.ItemText>
                      {it.hint && <span className="block truncate text-xs text-ink-faint">{it.hint}</span>}
                    </span>
                  </RS.Item>
                </Tooltip.Trigger>
                <SelectPreviewContent preview={it.thumbnailPreview} />
              </Tooltip.Root>
            ))}
          </RS.Viewport>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
    </Tooltip.Provider>
  )
}

function SelectPreview({ children, preview }: {
  children: ReactNode
  preview?: ReactNode
}) {
  if (!preview) return <>{children}</>
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <SelectPreviewContent preview={preview} />
    </Tooltip.Root>
  )
}

function SelectPreviewContent({ preview }: { preview?: ReactNode }) {
  if (!preview) return null
  return (
    <Tooltip.Portal>
      <Tooltip.Content
        side="left" sideOffset={10}
        className="z-[70] overflow-hidden rounded-[--radius-card] border border-line bg-surface-1 p-1 shadow-[0_16px_48px_oklch(0_0_0/0.55)]"
      >
        {preview}
      </Tooltip.Content>
    </Tooltip.Portal>
  )
}

export function Tabs({
  value, onValueChange, tabs,
}: {
  value: string
  onValueChange: (v: string) => void
  /** Counts are shown when supplied. Worth the space: they tell you which tab
   *  is worth opening before you open it, and make an empty one obvious
   *  rather than something you discover by clicking. */
  tabs: { value: string; label: string; count?: number }[]
}) {
  return (
    <RT.Root value={value} onValueChange={onValueChange}>
      <RT.List className="inline-flex gap-1 rounded-[--radius-control] border border-line bg-surface-1 p-1">
        {tabs.map(t => (
          <RT.Trigger
            key={t.value}
            value={t.value}
            className={cn(
              'flex items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-3.5 py-1.5',
              'text-sm font-medium text-ink-dim transition-colors hover:text-ink',
              'data-[state=active]:bg-surface-3 data-[state=active]:text-ink',
            )}
          >
            {t.label}
            {t.count != null && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[11px] tabular-nums',
                  t.value === value ? 'bg-brand/15 text-brand' : 'bg-surface-2 text-ink-faint',
                )}
              >
                {t.count}
              </span>
            )}
          </RT.Trigger>
        ))}
      </RT.List>
    </RT.Root>
  )
}
